import { graphql } from '@api/generated';
import { RecordingAction } from '@api/generated/graphql';
import { useMutation } from '@apollo/client';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import useService from '@modules/ServiceModule/useService';
import TasksModule from '@modules/TasksModule';
import ToastModule from '@modules/ToastModule';
import { useEffect, useMemo, useState } from 'react';
import getInitialRecordingState from '../lib/getInitialRecordingState';
import useCallState from '@modules/SoftPhoneModule/useCallState';

export enum RecordingStatus {
  STARTED = 'STARTED',
  NOT_STARTED = 'NOT_STARTED',
  PAUSED = 'PAUSED',
}

export type RecordingState = {
  status: RecordingStatus;
  disabled: boolean;
};

export const PERFORM_RECORDING_ACTION_MUTATION = graphql(`
  mutation performRecordingAction(
    $kazooCallId: String!
    $action: RecordingAction!
  ) {
    performRecordingAction(kazooCallId: $kazooCallId, action: $action)
  }
`);

const showRecordingError = () => {
  ToastModule.showToast({
    status: 'critical',
    title: 'Failed to perform recording action',
    toastBody: 'Please try again',
  });
};

const useRecording = () => {
  const { pcsConversationSid, queueIds, direction } = useCallState();
  const { queues } = useService();
  const { user } = useAuth();

  const initialRecordingState: RecordingState = useMemo(() => {
    if (direction === 'originating') {
      return {
        status: RecordingStatus.NOT_STARTED,
        disabled: false,
      };
    }

    // not outgoing, so the below covers incoming calls

    // incoming - internal calls
    if (queueIds.length === 0) {
      return {
        status: RecordingStatus.NOT_STARTED,
        disabled: true,
      };
    }

    const task = TasksModule.findTaskByPcsConversationSid(pcsConversationSid);

    const queueRecordingPreference = queues.find(
      (queue) => queue.id === task?.queueId,
    )?.inboundRecording;

    const initialRecordingState = getInitialRecordingState(
      queueRecordingPreference,
      user?.callRecording,
    );
    return initialRecordingState;
  }, [
    direction,
    pcsConversationSid,
    queueIds.length,
    queues,
    user?.callRecording,
  ]);

  const [recordingState, setRecordingState] = useState<RecordingState>(
    initialRecordingState,
  );

  const [mutate, { loading }] = useMutation(PERFORM_RECORDING_ACTION_MUTATION);

  const toggleRecording = async (kazooCallId: string) => {
    let action: RecordingAction;
    let newStatus: RecordingStatus;

    switch (recordingState.status) {
      case RecordingStatus.NOT_STARTED:
        action = RecordingAction.Start;
        newStatus = RecordingStatus.STARTED;
        break;
      case RecordingStatus.PAUSED:
        action = RecordingAction.Resume;
        newStatus = RecordingStatus.STARTED;
        break;
      case RecordingStatus.STARTED:
        action = RecordingAction.Pause;
        newStatus = RecordingStatus.PAUSED;
        break;
    }

    try {
      const result = await mutate({
        variables: {
          kazooCallId,
          action,
        },
      });

      if (result.data?.performRecordingAction) {
        setRecordingState({ ...recordingState, status: newStatus });
      } else {
        showRecordingError();
      }
    } catch (error) {
      showRecordingError();
    }
  };

  useEffect(() => {
    if (pcsConversationSid) {
      setRecordingState(initialRecordingState);
    }
  }, [initialRecordingState, pcsConversationSid]);

  return { toggleRecording, recordingLoading: loading, recordingState };
};

export default useRecording;
